import type { Ref } from 'react';
import Select from 'react-select';
import type { GroupBase, SelectInstance } from 'react-select';

import styled, { css } from '@app/styled';
import { oneLineEllipsis } from '@app/styles';
import { color, modifier } from '@app/theme';

import type { SelectFieldProps } from './Select';

export const focusOutlineVisibilityClassName = 'LouisSelect-focus_outline_should_be_visible';

export const withInputErrorModifier = modifier(
    'error',
    css`
        border-color: ${color('border.danger')};
    `,
);

const checkmark = (checkmarkColor: string) =>
    `url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 16 16' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M6.64372 10.1336L3.58684 7.07669L2.65918 8.01319L6.65255 12.0066L12.9872 5.65427L12.0507 4.71777L6.64372 10.1336Z' fill='${checkmarkColor}' /%3E%3C/svg%3E%0A")`;

export const Item = styled.div`
    ${oneLineEllipsis};
    // Padding is added, so MultiValueLabel Item text, could be seen in full size
    padding: 1px;
    font-size: 14px;
    line-height: 15px;
    max-width: 200px;
`;

export const CustomSelect = styled(Select)<{ error?: boolean }>`
    .react-select__control {
        border: 1px solid ${color('border.input.default')};
        ${withInputErrorModifier};
        border-radius: 0px;
        font-size: 15px;
        line-height: 20px;
        min-height: 45px;
        outline: none;
        box-shadow: none;
        cursor: pointer;

        &:hover {
            border-color: ${color('border.input.hover')};
            ${withInputErrorModifier};
        }

        &:focus-within,
        [data-dev-state='focus'] & {
            border-color: ${color('border.input.hover')};
        }
    }

    .react-select__menu-list {
        border: 1px solid ${color('border.input.default')};
    }

    .react-select__control--is-focused,
    .react-select__control--is-focused:focus {
        border-color: ${color('border.input.hover')};
        outline: none;
        box-shadow: none;
    }

    .react-select__option {
        cursor: pointer;
        font-size: 14px;
    }

    .react-select__option--is-selected {
        cursor: pointer;

        &::before {
            width: 16px;
            height: 16px;
            content: '';
            display: inline-block;
            vertical-align: middle;
            margin-right: 4px;
            background-size: 16px 16px;
            background-repeat: no-repeat;
            background-position: center;
            background-image: ${checkmark('black')};
        }

        &:hover {
            &::before {
                background-image: ${checkmark('white')};
            }
        }
    }

    .react-select__option--is-focused {
        background-color: inherit;
        color: inherit;
    }

    &&& .react-select__input {
        &:focus {
            outline: none !important;
        }
    }

    .react-select__value-container {
        //Padding like that, because otherwise childs in isMulti are jumping when 2 lines of them are on the screen
        padding: 6px 20px 7px 15px;
    }

    .react-select__option--is-selected {
        background-color: white;
        color: black;
    }

    .react-select__multi-value__label {
        background-color: ${color('background.surface.middle')};
    }

    .react-select__multi-value__remove {
        background-color: ${color('background.surface.middle')};
        color: #808080;

        &:hover {
            background-color: ${color('background.surface.middle')};
            color: ${color('background.action.primary.default')};
        }
    }

    .react-select__dropdown-indicator--is-selected {
        color: ${color('background.action.primary.default')};
    }

    .react-select__dropdown-indicator {
        padding: 0 10px 0 0;
        color: ${color('text.light.default')};
        fill: currentColor;
        height: 23px;

        &:hover {
            color: ${color('border.input.hover')};
        }
    }

    .react-select__control--menu-is-open .react-select__dropdown-indicator {
        transform: scaleY(-1);
    }

    .react-select__indicator-separator {
        display: none;
    }

    .react-select__clear-indicator {
        color: ${color('text.light.default')};
        fill: currentColor;
        height: 23px;

        &:hover {
            color: ${color('border.input.hover')};
        }
    }

    .react-select__menu {
        margin-top: 4px;
    }

    .react-select__menu-list {
        max-height: 282px;
    }
` as <
    Async extends boolean,
    Creatable extends boolean,
    OptionType,
    IsMulti extends boolean = false,
    GroupType extends GroupBase<OptionType> = GroupBase<OptionType>,
>(
    props: SelectFieldProps<Async, Creatable, OptionType, IsMulti, GroupType> & {
        ref?: Ref<SelectInstance>;
    },
) => ReturnType<typeof Select>;

export const Container = styled.div`
    width: 100%;
    display: grid;
    gap: 5px;
    grid-template-columns: minmax(50px, 1fr); /* This ensures Select doesn't overflow 100% */

    &.${focusOutlineVisibilityClassName} .react-select__option--is-focused {
        background-color: ${color('background.action.primary.default')};
        color: white;
    }
    &:not(.${focusOutlineVisibilityClassName}) .react-select__option:hover {
        background-color: ${color('background.action.primary.default')};
        color: white;
    }
`;
